import { forwardRef, useId } from 'react';
import type { TextareaHTMLAttributes } from 'react';
import type { FieldError } from 'react-hook-form';
import { cn } from '@/lib/utils';

interface TextAreaFieldProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: FieldError;
  containerClassName?: string;
  /**
   * 'default' — legacy 2px-radius textarea.
   * 'lms' — Figma "LMS Portal - Updated" textarea, matching FormField's lms
   * input: N600 label, rounded-8 Card/Stroke 3 border, 16/24 text.
   */
  variant?: 'default' | 'lms';
}

export const TextAreaField = forwardRef<
  HTMLTextAreaElement,
  TextAreaFieldProps
>(
  (
    {
      label,
      error,
      containerClassName = '',
      className = '',
      rows = 4,
      variant = 'default',
      id,
      ...props
    },
    ref
  ) => {
    // Associate label and textarea (clickable label, reachable by label text).
    const generatedId = useId();
    const textareaId = id ?? generatedId;

    const labelClasses =
      variant === 'lms'
        ? 'block mb-1.5 text-body-regular text-neutral-n600'
        : 'block mb-1 text-body-regular text-character-title-85';

    const textareaClasses =
      variant === 'lms'
        ? 'w-full outline-none font-roboto text-[16px] leading-6 text-black rounded-lg border border-lms-border bg-white px-4 py-3 focus:border-primary-6 transition-colors resize-y disabled:bg-lms-disabled-bg disabled:text-neutral-n400 disabled:cursor-not-allowed'
        : 'w-full outline-none text-body-regular text-character-title-85 rounded-[2px] border border-neutral-100 bg-neutral-0 px-3 py-2 resize-y';

    return (
      <div className={containerClassName}>
        <label htmlFor={textareaId} className={labelClasses}>
          {label}
          {variant === 'lms' && props.required && (
            <span className="text-lms-red-hover"> *</span>
          )}
        </label>
        <textarea
          ref={ref}
          id={textareaId}
          rows={rows}
          className={cn(textareaClasses, className)}
          {...props}
        />
        {error && (
          <p className="mt-1 text-footnote-description text-error-500">
            {error.message}
          </p>
        )}
      </div>
    );
  }
);

TextAreaField.displayName = 'TextAreaField';
